import { Star, Phone, Mail, MapPin, Users, Clock } from 'lucide-react';
import type { Supplier, SupplierCategory } from '@yawi/types';
import { useI18n } from '@/shared/i18n';

interface SupplierCardProps {
  supplier: Supplier;
  onClick?: () => void;
}

const categoryColors: Record<SupplierCategory, { bg: string; text: string }> = {
  PLOMERIA: { bg: 'bg-blue-100', text: 'text-blue-700' },
  ELECTRICIDAD: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  JARDINERIA: { bg: 'bg-green-100', text: 'text-green-700' },
  LIMPIEZA: { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  MANTENIMIENTO: { bg: 'bg-slate-100', text: 'text-slate-700' },
  SEGURIDAD: { bg: 'bg-red-100', text: 'text-red-700' },
  FUMIGACION: { bg: 'bg-purple-100', text: 'text-purple-700' },
  MUDANZAS: { bg: 'bg-orange-100', text: 'text-orange-700' },
  REPARACIONES: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  CERRAJERIA: { bg: 'bg-amber-100', text: 'text-amber-700' },
  PINTURA: { bg: 'bg-pink-100', text: 'text-pink-700' },
  CARPINTERIA: { bg: 'bg-stone-100', text: 'text-stone-700' },
  AIRE_ACONDICIONADO: { bg: 'bg-sky-100', text: 'text-sky-700' },
  ALBERCAS: { bg: 'bg-teal-100', text: 'text-teal-700' },
  IMPERMEABILIZACION: { bg: 'bg-violet-100', text: 'text-violet-700' },
  OTRO: { bg: 'bg-gray-100', text: 'text-gray-700' },
};

export const SupplierCard = ({ supplier, onClick }: SupplierCardProps) => {
  const { t } = useI18n();
  const categoryStyle = categoryColors[supplier.category] || categoryColors.OTRO;

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-3 h-3 ${i < Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300'}`}
      />
    ));
  };

  return (
    <div
      className={`bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer ${
        !supplier.isActive ? 'opacity-60' : ''
      }`}
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-slate-800 truncate">{supplier.name}</h3>
            {!supplier.isActive && (
              <span className="px-2 py-0.5 bg-slate-200 text-slate-600 text-xs rounded-full">
                {t('common.inactive')}
              </span>
            )}
          </div>
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${categoryStyle.bg} ${categoryStyle.text}`}>
            {t(`suppliers.category.${supplier.category.toLowerCase()}`)}
          </span>
        </div>
        <div className="flex items-center gap-1 ml-2">
          {renderStars(Number(supplier.rating) || 0)}
          <span className="text-sm text-slate-600 ml-1">{(Number(supplier.rating) || 0).toFixed(1)}</span>
        </div>
      </div>

      {/* Description */}
      {supplier.description && (
        <p className="text-sm text-slate-600 line-clamp-2 mb-3">{supplier.description}</p>
      )}

      {/* Contact Info */}
      <div className="space-y-1 text-xs text-slate-500">
        {supplier.contactName && (
          <div className="flex items-center gap-2">
            <Users className="w-3 h-3" />
            <span>{supplier.contactName}</span>
          </div>
        )}
        {supplier.contactPhone && (
          <a href={`tel:${supplier.contactPhone}`} className="flex items-center gap-2 hover:text-primary-600">
            <Phone className="w-3 h-3" />
            <span>{supplier.contactPhone}</span>
          </a>
        )}
        {supplier.contactEmail && (
          <a href={`mailto:${supplier.contactEmail}`} className="flex items-center gap-2 hover:text-primary-600">
            <Mail className="w-3 h-3" />
            <span className="truncate">{supplier.contactEmail}</span>
          </a>
        )}
        {supplier.address && (
          <div className="flex items-center gap-2">
            <MapPin className="w-3 h-3" />
            <span className="truncate">{supplier.address}</span>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
        <div className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          <span>{supplier.totalVisits} {t('suppliers.visits')}</span>
        </div>
      </div>
    </div>
  );
};
